/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { AsyncPipe } from '@angular/common';
import {
    AfterContentChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Inject,
    OnDestroy,
    OnInit
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FlexDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';
import { Actions, Store } from '@ngxs/store';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';
import { RedlineConfigService } from '../../../components/redline/services/redline-config.service';
import { FlexiblePanelComponent } from '../../../core/ui/flexible-panel/flexible-panel.component';
import { MapConfigService } from '../../map/services/map-config.service';
import { QueryService } from '../../map/services/query.service';
import { SelectionFeatures } from '../../map/state/selection.model';
import { SelectionState } from '../../map/state/selection.state';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { DeviceService } from '../../shared/services/device.service';
import { LoggerService } from '../../shared/services/logger.service';
import { ReportSideTabsComponent } from '../interface/report-side-tabs/report-side-tabs.component';
import { ReportSelectorComponent } from '../interface/reportSelector/report-selector.component';
import { FeatureReportComponent } from '../reportTypes/featureReport/feature-report.component';
import { ReportService } from '../services/report.service';
import { ResetReportPagingAndSort } from '../state/report.actions';
import { FeatureReportCollection, ReportSubtypeValue } from '../state/report.model';
import { ReportState } from '../state/report.state';
import { ReportDisplayBaseComponent } from './report-display-base.component';
import { ReportDisplayOverlayRef } from './report-display.overlay-ref';
import { debounce } from 'lodash';

@Component({
    selector: 'dash-gis-report-display',
    templateUrl: './feature-report-display.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FlexiblePanelComponent,
        LayoutDirective,
        FlexDirective,
        ReportSideTabsComponent,
        ReportSelectorComponent,
        FeatureReportComponent,
        AsyncPipe,
    ],
})
export class FeatureReportDisplayComponent
    extends ReportDisplayBaseComponent
    implements OnInit, AfterContentChecked, OnDestroy
{
    activeFeatureReports$: Observable<FeatureReportCollection> = inject(Store).select(ReportState.activeFeatureReports);
    selection$: Observable<SelectionFeatures> = inject(Store).select(SelectionState.selection);

    activeLayers$: Observable<string[]>;
    initialWidth$ = new Subject<number>();
    activeLayer$ = new BehaviorSubject<string>('');
    shouldDisableScroll$: Observable<boolean>;

    onWidthUpdate = debounce(this.widthUpdateInternal.bind(this), 200, {});

    protected historyGroup = 'featureReports';

    private overrideSubtype$ = new BehaviorSubject<ReportSubtypeValue>(null);

    public ngOnInit() {
        super.ngOnInit();
        this.initialWidth$.pipe(takeUntil(this.dispose$)).subscribe((width) => {
            if (this.overlayRef.hostElement) {
                this.panelWidth$.next(`${width}px`);
                this.overlayRef.updateSize({ width: `${width}px` });
                this.overlayRef.updatePosition();
                this.cdr.detectChanges();
            }
        });

        this.activeFeatureReports$.pipe(takeUntil(this.dispose$)).subscribe((reports) => {
            if (Object.keys(reports).length === 0) {
                this.closePanel.emit();
            }
        });

        this.selection$
            .pipe(distinctUntilChanged(isEqual), takeUntil(this.dispose$))
            .subscribe(() => this.store.dispatch(new ResetReportPagingAndSort({ id: this.uid })));
    }

    public ngAfterContentChecked() {
        super.ngAfterContentChecked();
    }

    public ngOnDestroy() {
        super.ngOnDestroy();
    }

    public constructor(
        logger: LoggerService,
        mapConfig: MapConfigService,
        redlineConfig: RedlineConfigService,
        reportService: ReportService,
        queryService: QueryService,
        deviceService: DeviceService,
        dialog: MatDialog,
        actions$: Actions,
        store: Store,
        cdr: ChangeDetectorRef,
        @Inject(ReportDisplayOverlayRef) overlayRef: ReportDisplayOverlayRef
    ) {
        super(
            logger,
            mapConfig,
            redlineConfig,
            reportService,
            queryService,
            deviceService,
            dialog,
            actions$,
            store,
            cdr,
            overlayRef
        );

        this.activeLayers$ = this.activeFeatureReports$.pipe(
            distinctUntilChanged(isEqual),
            map((activeReports) => Object.keys(activeReports))
        );

        this.shouldDisableScroll$ = this.activeLayers$.pipe(map((layers) => layers.length > 1));

        combineLatest([this.activeFeatureReports$, this.activeLayer$, this.overrideSubtype$])
            .pipe(
                distinctUntilChanged(isEqual),
                filter(([, , overrideSubtype]) => overrideSubtype !== 'pending'),
                map(([reports, layer, overrideSubtype]) => {
                    let isDetail = false;
                    let overrideWidth: number = null;
                    const layers = Object.getOwnPropertyNames(reports);
                    if (DashGisUtil.isObject(reports)) {
                        let layerReports;
                        if (!layer) {
                            layerReports = reports[layers[0]];
                        } else {
                            layerReports = reports[layer];
                        }
                        if (layerReports) {
                            if (layerReports.length > 0) {
                                overrideWidth = layerReports[0].definition.displayWidth;
                            }
                            if (layerReports.length === 1) {
                                const subtype = overrideSubtype || layerReports[0].definition.subType;
                                isDetail = subtype === 'detail' || subtype === 'pending';
                            }
                        }
                    }
                    let baseWidth: number;
                    if (overrideWidth) {
                        baseWidth = overrideWidth;
                    } else {
                        baseWidth = isDetail ? 450 : 900;
                    }
                    return baseWidth + (layers.length > 1 ? 170 : 0);
                }),
                tap((width) => this.initialWidth$.next(width)),
            ).subscribe();
    }

    setActiveLayer(layerName: string) {
        this.activeLayer$.next(layerName);
    }

    setSubtype(overrideSubtype: ReportSubtypeValue) {
        this.overrideSubtype$.next(overrideSubtype);
    }

    private widthUpdateInternal(width: number) {
        this.panelWidth$.next(`${width}px`);
        this.overlayRef.updateSize({ width: `${width}px` });
        this.overlayRef.updatePosition();
        this.cdr.detectChanges();
    }
}
